import { useEffect } from 'react'
import { useAuthStore } from '../../services/authStore'
import realtimeEventService from '../../services/realtimeEventService'
import useNotificationStore from '../../services/notificationStore'
import notificationService from '../../services/notificationService'
import NotificationToastViewport from './NotificationToastViewport'
import NotificationBannerHost from './NotificationBannerHost'
import NotificationLiveRegion from './NotificationLiveRegion'

export default function NotificationProvider({ children }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isAdmin = Boolean(useAuthStore((state) => state.user?.is_admin ?? state.user?.isAdmin))

  const loadInitialNotifications = useNotificationStore((state) => state.loadInitialNotifications)
  const handleRealtimeEvent = useNotificationStore((state) => state.handleRealtimeEvent)
  const setRealtimeStatus = useNotificationStore((state) => state.setRealtimeStatus)
  const resetActorState = useNotificationStore((state) => state.resetActorState)
  const upsertOperation = useNotificationStore((state) => state.upsertOperation)

  useEffect(() => {
    if (!isAuthenticated) {
      realtimeEventService.stop()
      resetActorState()
      return undefined
    }

    let active = true

    const bootstrap = async () => {
      try {
        await loadInitialNotifications()
      } catch {
        // Initial list load failure should not block realtime startup.
      }
    }

    const unsubscribeEvents = realtimeEventService.subscribe((event) => {
      if (!active) return
      handleRealtimeEvent(event)
    })

    const unsubscribeStatus = realtimeEventService.subscribeStatus((status) => {
      if (!active) return
      setRealtimeStatus(status)
    })

    realtimeEventService.setPollingFallbackHandler(async () => {
      if (!active) return
      try {
        await loadInitialNotifications()
        if (isAdmin) {
          const operations = await notificationService.listBackgroundOperations({ limit: 50 })
          ;(operations || []).forEach((operation) => upsertOperation(operation))
        }
      } catch {
        // Fallback polling should be resilient to transient API failures.
      }
    })

    void bootstrap()
    realtimeEventService.start()

    return () => {
      active = false
      unsubscribeEvents()
      unsubscribeStatus()
      realtimeEventService.stop()
    }
  }, [isAuthenticated, isAdmin, loadInitialNotifications, handleRealtimeEvent, setRealtimeStatus, resetActorState, upsertOperation])

  return (
    <>
      {children}
      <NotificationBannerHost />
      <NotificationToastViewport />
      <NotificationLiveRegion />
    </>
  )
}
